import { Key, ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { TmdxAvatar, Chip, Tooltip } from '@ds';
import { TableSwitch, TableColumnType } from '@ui';
import { MdInfoOutline } from 'react-icons/md';
import { AffiliationType, EmployeeComposed, EmployeeListFilters, EmployeeStatus } from '@/api/apis/resources';
import { getFullName, getLocationAddress, getRoleNames } from '@/utils/helpers';
import { col } from '../../utils';

export const CONTRACTOR_LIST_FILTERS_BASE: EmployeeListFilters = {
  employeeIds: [],
  roles: [],
  baseLocation: [],
  status: EmployeeStatus.All,
  isContractor: true,
};

const CONTRACTOR_COLUMNS: TableColumnType[] = [
  col('name', 'TMDX Contractor Name', {
    sortable: true,
    minWidthClassName: 'min-w-60',
  }),
  col('role', 'Role', {
    sortable: false,
    minWidthClassName: 'min-w-60',
  }),
  col('position', 'Position', {
    sortable: true,
    minWidthClassName: 'min-w-60',
  }),
  col('locationName', 'Home Location', {
    sortable: false,
    minWidthClassName: 'min-w-80',
  }),
  col('affiliation', 'Affiliation', {
    sortable: false,
    minWidthClassName: 'min-w-80',
  }),
  col('email', 'Email', {
    sortable: false,
    minWidthClassName: 'min-w-60',
  }),
  col('phone', 'Phone', {
    sortable: false,
    minWidthClassName: 'min-w-60',
  }),
  col('isActive', 'Active', {
    sortable: false,
    minWidthClassName: 'min-w-28',
  }),
];

export const getContractorsTableColumns = (): TableColumnType[] => {
  return [...CONTRACTOR_COLUMNS];
};

export const getRenderContractorsTableCell =
  (
    onIsActiveChanged: (employee: EmployeeComposed) => void,
    hasEmployeeEditPermission: boolean,
    resolveSubdivisionName?: (stateCode?: string | null, countryCode?: string | null) => string | undefined,
  ) =>
  (employee: EmployeeComposed, columnKey: Key): ReactNode => {
    const {
      id,
      firstName,
      lastName,
      profilePhoto,
      roles,
      position,
      email,
      phone,
      isActive,
      affiliation,
      affiliationTxCenter,
      affiliationOpo,
      homeLocation,
    } = employee;

    const employeeRoles = getRoleNames(roles);
    const homeLocationAddress = getLocationAddress(homeLocation, resolveSubdivisionName);

    switch (columnKey) {
      case 'name':
        return (
          <Link to={`/admin/users/contractors/${id}`} className="flex items-center gap-2">
            <TmdxAvatar radius="xl" size="xl" name={getFullName(firstName, lastName)} src={profilePhoto} />
            <span className="text-m-m">{getFullName(firstName, lastName)}</span>
          </Link>
        );
      case 'role':
        return (
          <div className="flex items-center gap-2 truncate">
            {employeeRoles.map((role, index) => {
              if (!index) {
                return <Chip key={`main-${role}`}>{role}</Chip>;
              }

              if (index === 1) {
                const slicedItems = employeeRoles.slice(index);
                const rolesName = slicedItems.map((employeeRole) => employeeRole).join(', ');

                return (
                  <Tooltip placement="top" className="max-w-64" content={rolesName}>
                    <Chip key="additionalRolesCount">{`+${employeeRoles.length - index}`}</Chip>
                  </Tooltip>
                );
              }

              return null;
            })}
          </div>
        );
      case 'position':
        return (
          <div className="flex items-center gap-2">
            <span className="text-m-m">{position}</span>
          </div>
        );
      case 'locationName':
        return (
          <div className="flex items-center gap-2">
            <span className="text-m-m">{homeLocationAddress || '-'}</span>
          </div>
        );
      case 'affiliation':
        switch (affiliation?.affiliationType) {
          case AffiliationType.NoContract:
            return (
              <div className="flex items-center gap-2">
                <span className="text-m-m">NO CONTRACT</span>
              </div>
            );
          case AffiliationType.TMDXContract:
            return (
              <div className="flex items-center gap-2">
                <span className="text-m-m">TMDX CONTRACT</span>
              </div>
            );
          case AffiliationType.TXCenter:
            return affiliationTxCenter ? (
              <Tooltip
                placement="top"
                content={`Address: ${getLocationAddress(affiliationTxCenter.location, resolveSubdivisionName)}`}
              >
                <div className="flex flex-col">
                  <span className="text-m-m">TXC</span>
                  <div className="flex text-m-m gap-2 items-center">
                    {affiliationTxCenter.code}
                    <MdInfoOutline size={16} className="text-base-40 mb-px" />
                  </div>
                </div>
              </Tooltip>
            ) : (
              '-'
            );
          case AffiliationType.OPO:
            return affiliationOpo ? (
              <Tooltip content={`Address: ${getLocationAddress(affiliationOpo.location, resolveSubdivisionName)}`}>
                <div className="flex flex-col">
                  <span className="text-m-m">OPO</span>
                  <div className="flex text-m-m gap-2 items-center">
                    {affiliationOpo.code}
                    <MdInfoOutline size={16} className="text-base-40 mb-px" />
                  </div>
                </div>
              </Tooltip>
            ) : (
              '-'
            );
          default:
            return (
              <div className="flex items-center gap-2">
                <span className="text-m-m">-</span>
              </div>
            );
        }
      case 'email':
        return <span className="text-m-r">{email}</span>;
      case 'phone':
        return <span className="text-m-r">{phone}</span>;
      case 'isActive':
        return (
          <TableSwitch
            isActive={isActive}
            isDisabled={!hasEmployeeEditPermission}
            action={() => onIsActiveChanged(employee)}
          />
        );
      default:
        return null;
    }
  };
